// Waveform peaks for the catalogue rows, computed in the browser from the
// preview audio the plugin already embeds in each pattern.
//
// The server cannot do this: it deliberately never decodes preview audio
// (see extract_preview_audio in backend/services/preset_render.py - no
// ffmpeg, no attacker-supplied audio in a subprocess). The browser already
// decodes the same file to play it, so it computes the peaks too, once per
// kit per page load, and keeps only a small array of numbers.

export const PEAK_BINS = 720;
// A catalogue page can hold 48 rows; decoding them all at once would stall
// the main thread and flood the network. Rows ask as they scroll into view
// (see KitWaveform), and at most this many decodes run at a time.
const MAX_CONCURRENT = 3;

// Min/max per bin across all channels, then scaled so the loudest point
// reaches 1 - the plugin's Normalize view, so a quiet loop still draws a
// readable shape. Returns { min, max } Float32Arrays of length `bins`.
export function computePeaks(channels, bins = PEAK_BINS) {
  const min = new Float32Array(bins);
  const max = new Float32Array(bins);
  const length = channels.length > 0 ? channels[0].length : 0;
  if (length === 0) return { min, max };

  let loudest = 0;
  for (let bin = 0; bin < bins; bin += 1) {
    const start = Math.floor((bin * length) / bins);
    const end = Math.max(start + 1, Math.floor(((bin + 1) * length) / bins));
    let lo = 0;
    let hi = 0;
    for (const data of channels) {
      for (let i = start; i < end && i < length; i += 1) {
        const v = data[i];
        if (v < lo) lo = v;
        if (v > hi) hi = v;
      }
    }
    min[bin] = lo;
    max[bin] = hi;
    loudest = Math.max(loudest, -lo, hi);
  }

  if (loudest > 0) {
    const gain = 1 / loudest;
    for (let bin = 0; bin < bins; bin += 1) {
      min[bin] *= gain;
      max[bin] *= gain;
    }
  }
  return { min, max };
}

const cache = new Map(); // url -> Promise<{ min, max, duration }>
const queue = [];
let running = 0;

function pump() {
  while (running < MAX_CONCURRENT && queue.length > 0) {
    const job = queue.shift();
    running += 1;
    job().finally(() => {
      running -= 1;
      pump();
    });
  }
}

async function decode(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`preview ${res.status}`);
  const bytes = await res.arrayBuffer();
  // An OfflineAudioContext decodes without an audio output, so this never
  // trips the autoplay policy and needs no user gesture. The callback form of
  // decodeAudioData is the one every supported browser accepts.
  const Ctx = window.OfflineAudioContext || window.webkitOfflineAudioContext;
  if (!Ctx) throw new Error("no Web Audio");
  const ctx = new Ctx(1, 1, 44100);
  const buffer = await new Promise((resolve, reject) => ctx.decodeAudioData(bytes, resolve, reject));
  const channels = [];
  for (let c = 0; c < buffer.numberOfChannels; c += 1) channels.push(buffer.getChannelData(c));
  return { ...computePeaks(channels), duration: buffer.duration };
}

export function loadPeaks(url) {
  if (!cache.has(url)) {
    const promise = new Promise((resolve, reject) => {
      queue.push(() => decode(url).then(resolve, reject));
      pump();
    });
    // A failure is not cached: the next time the row scrolls in, it retries.
    promise.catch(() => cache.delete(url));
    cache.set(url, promise);
  }
  return cache.get(url);
}
